//Proofed
import { useState, useRef, useMemo, type MouseEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    AppBar, Toolbar, Box, Button, IconButton, Typography,
    Menu, MenuItem, Drawer, List, ListItemButton, ListItemText,
    Divider, useTheme, useMediaQuery, ListItemIcon, Collapse,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import InsightsIcon from '@mui/icons-material/Insights'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import DomainIcon from '@mui/icons-material/Domain'
import AssessmentIcon from '@mui/icons-material/Assessment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import LanguageIcon from '@mui/icons-material/Language'
import HandshakeIcon from '@mui/icons-material/Handshake'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import GroupsIcon from '@mui/icons-material/Groups'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import type { ComponentType } from 'react'
import type { SvgIconProps } from '@mui/material'
import cfpLogo from '../img/cfplogo2.svg'
import { buildCoreUrl, loginpage, readCookie } from '../common/common'
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_TERTIARY, COLOR_QUATERNARY, BORDER_BLACK, COLOR_SOLUTIONS } from '../theme'
import { FancyGoldButton } from './FancyGoldButton'
import { FancyLightButton } from './FancyLightButton'

// ── Referral partner logos ─────────────────────────────────────────────────────
const PARTNER_LOGOS: Record<string, { src: string; label: boolean }> = {
    RIC: { src: '/partner-logos/ric-cfp.png', label: false },
    BDG: { src: '/partner-logos/business-daily-group.png', label: true },
    FM: { src: '/partner-logos/FMbusinessdaily.png', label: true },
}

function getRefCode(): string | null {
    return document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith('refcode='))
        ?.split('=')[1]
        ?.trim()
        ?.toUpperCase() ?? null
}

// ── Nav data ──────────────────────────────────────────────────────────────────
interface Product {
    name: string
    tagline: string
    description: string
    path: string
    Icon: ComponentType<SvgIconProps>
    color: string
}

const products: Product[] = [
    {
        name: 'Core',
        tagline: 'Tender Alerting',
        description: 'Real-time alerts across every UK public sector portal',
        path: '/products/core',
        Icon: NotificationsActiveIcon,
        color: COLOR_PRIMARY,
    },
    {
        name: 'Analytics',
        tagline: 'Market Intelligence',
        description: 'Forecast the public sector for your best sales opportunities',
        path: '/products/analytics',
        Icon: InsightsIcon,
        color: COLOR_QUATERNARY,
    },
    {
        name: 'Tender Ninja',
        tagline: 'Faster Bid Writing',
        description: 'Write stronger bids faster with safe AI that works from your documents',
        path: '/products/tender-ninja',
        Icon: AutoAwesomeIcon,
        color: COLOR_SECONDARY,
    },
    {
        name: 'Bid Services',
        tagline: 'Expert Bid Support',
        description: 'Access bid writers, prep support and process optimisation',
        path: '/products/bid-services',
        Icon: HandshakeIcon,
        color: COLOR_TERTIARY,
    },
]


const solutionGroups = [
    {
        group: 'Getting Started',
        color: COLOR_SOLUTIONS,
        items: [
            {
                title: 'New to Government Contracts',
                description: 'Find early opportunities and build your winning track record',
                path: '/solutions/new-to-government', Icon: RocketLaunchIcon, color: COLOR_SOLUTIONS
            },
            {
                title: 'Grow Faster in UK Public Sector',
                description: 'Identify opportunities and grow faster in the government market',
                path: '/solutions/grow-faster-uk-public-sector', Icon: DomainIcon, color: COLOR_SOLUTIONS
            },
            {
                title: 'Market Analysis & Fit',
                description: 'Analyse the market to guide strategic decisions',
                path: '/solutions/market-analysis-and-fit', Icon: AssessmentIcon, color: COLOR_SOLUTIONS
            },
        ],
    },
    {
        group: 'Expanding Reach',
        color: COLOR_TERTIARY,
        items: [
            {
                title: 'Expand into New Sectors',
                description: 'Spot cross-sector opportunities and manage your growing pipeline',
                path: '/solutions/expand-into-new-sectors', Icon: AccountTreeIcon, color: COLOR_TERTIARY
            },
            {
                title: 'Grow into Europe & Overseas',
                description: 'Track international markets and plan cross-border growth effectively',
                path: '/solutions/grow-into-europe-and-overseas', Icon: LanguageIcon, color: COLOR_TERTIARY
            },
        ],
    },
    {
        group: 'Execution Support',
        color: COLOR_QUATERNARY,
        items: [
            {
                title: 'Improve Efficiency & Win Rates',
                description: 'Target smarter and improve results across every submission',
                path: '/solutions/improve-efficiency-and-win-rates', Icon: TrendingUpIcon, color: COLOR_QUATERNARY
            },
            {
                title: 'Find a Dedicated Bid Partner',
                description: 'Get matched with experienced specialists in your sector',
                path: '/solutions/find-a-dedicated-bid-partner', Icon: HandshakeIcon, color: COLOR_QUATERNARY
            },
        ],
    },
]

const successNetwork = [
    // {
    //     name: 'Customer Success',
    //     description: 'Real results from organisations winning with our support', path: '/success-network/customer-success', Icon: EmojiEventsIcon,
    //     color: COLOR_SECONDARY
    // },
    {
        name: 'Business Winning Network',
        description: 'Access experienced specialists across sectors and disciplines', path: '/success-network/business-winning-network', Icon: GroupsIcon, color: COLOR_PRIMARY
    },
    {
        name: 'Global Opportunities',
        description: 'International bids with our Vietnam engagement', path: '/success-network/global-opportunities', Icon: TravelExploreIcon, color: COLOR_TERTIARY
    },
    {
        name: 'Proven Approach',
        description: 'Our methodology for stronger pipelines and winning bids', path: '/success-network/proven-approach', Icon: AutoGraphIcon, color: COLOR_QUATERNARY
    },
]


interface NavLink {
    label: string
    path: string
    live?: boolean
}

const navLinks: NavLink[] = [
    { label: 'Pricing', path: '/pricing' },
    { label: 'Tenders', path: '/tenderresults', live: true },
]

// ── Component ─────────────────────────────────────────────────────────────────
export default function Navbar() {
    const muiTheme = useTheme()
    // Three nav modes:
    //   < sm (640px)       → hamburger drawer (isMobile)
    //   sm → lg (640–1023) → compact top-bar: Solutions + Network merged into one dropdown
    //   lg+ (1024px+)      → full top-bar with three separate dropdowns
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))
    const isCompact = useMediaQuery(muiTheme.breakpoints.down('lg')) && !isMobile
    const location = useLocation()

    const refPartner = useMemo(() => {
        const code = getRefCode()
        return code ? (PARTNER_LOGOS[code] ?? null) : null
    }, [])

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [productsAnchor, setProductsAnchor] = useState<null | HTMLElement>(null)
    const [solutionsAnchor, setSolutionsAnchor] = useState<null | HTMLElement>(null)
    const [successNetworkAnchor, setSuccessNetworkAnchor] = useState<null | HTMLElement>(null)
    const [combinedAnchor, setCombinedAnchor] = useState<null | HTMLElement>(null)
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false)
    const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false)
    const [mobileSuccessNetworkOpen, setMobileSuccessNetworkOpen] = useState(false)

    const productsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const solutionsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const successNetworkTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const combinedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const productsOpenedAt = useRef(0)
    const solutionsOpenedAt = useRef(0)
    const successNetworkOpenedAt = useRef(0)
    const combinedOpenedAt = useRef(0)

    const cancelPendingOpens = () => {
        if (productsTimer.current) clearTimeout(productsTimer.current)
        if (solutionsTimer.current) clearTimeout(solutionsTimer.current)
        if (successNetworkTimer.current) clearTimeout(successNetworkTimer.current)
        if (combinedTimer.current) clearTimeout(combinedTimer.current)
    }
    const openProducts = (e: MouseEvent<HTMLElement>) => {
        cancelPendingOpens()
        setSolutionsAnchor(null)
        setSuccessNetworkAnchor(null)
        const target = e.currentTarget
        productsTimer.current = setTimeout(() => {
            productsOpenedAt.current = Date.now()
            setProductsAnchor(target)
        }, 150)
    }
    const closeProducts = (delay = 0) => {
        if (productsTimer.current) clearTimeout(productsTimer.current)
        productsTimer.current = setTimeout(() => setProductsAnchor(null), delay)
    }
    const openSolutions = (e: MouseEvent<HTMLElement>) => {
        cancelPendingOpens()
        setProductsAnchor(null)
        setSuccessNetworkAnchor(null)
        const target = e.currentTarget
        solutionsTimer.current = setTimeout(() => {
            solutionsOpenedAt.current = Date.now()
            setSolutionsAnchor(target)
        }, 150)
    }
    const closeSolutions = (delay = 0) => {
        if (solutionsTimer.current) clearTimeout(solutionsTimer.current)
        solutionsTimer.current = setTimeout(() => setSolutionsAnchor(null), delay)
    }
    const openSuccessNetwork = (e: MouseEvent<HTMLElement>) => {
        cancelPendingOpens()
        setProductsAnchor(null)
        setSolutionsAnchor(null)
        const target = e.currentTarget
        successNetworkTimer.current = setTimeout(() => {
            successNetworkOpenedAt.current = Date.now()
            setSuccessNetworkAnchor(target)
        }, 150)
    }
    const closeSuccessNetwork = (delay = 0) => {
        if (successNetworkTimer.current) clearTimeout(successNetworkTimer.current)
        successNetworkTimer.current = setTimeout(() => setSuccessNetworkAnchor(null), delay)
    }
    const openCombined = (e: MouseEvent<HTMLElement>) => {
        cancelPendingOpens()
        setProductsAnchor(null)
        const target = e.currentTarget
        combinedTimer.current = setTimeout(() => {
            combinedOpenedAt.current = Date.now()
            setCombinedAnchor(target)
        }, 150)
    }
    const closeCombined = (delay = 0) => {
        if (combinedTimer.current) clearTimeout(combinedTimer.current)
        combinedTimer.current = setTimeout(() => setCombinedAnchor(null), delay)
    }

    const isActive = (path: string) => location.pathname === path
    const isProductsActive = products.some(p => location.pathname === p.path)
    const isSolutionsActive = location.pathname.indexOf('/solutions/') >= 0;
    const isNetworkActive = location.pathname.indexOf('/success-network/') >= 0;
    const loginHref = buildCoreUrl(readCookie('cfp_access_token') ? '/' : loginpage)
    // Nav button sizing — tighter in compact mode to fit everything on one line
    const menuitemspx = isCompact ? 1.5 : 2
    const buttongap = isCompact ? 2 : 2;
    const regiserpx = isCompact ? 1.5 : 1.5;
    const regiserpy = isCompact ? 0.5 : 0.75;
    const loginpx = isCompact ? 1.5 : 2.5;
    const loginpy = isCompact ? 0.5 : 0.75;

    return (
        <>
            <AppBar position="sticky" elevation={0}>
                <Toolbar sx={{ height: 74, px: { xs: 2, md: 2, lg: 4 }, gap: isCompact ? 0.5 : 1 }}>
                    {/* Logo */}
                    <Box
                        component={Link}
                        to="/"
                        sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: isCompact ? 1 : 3, flexShrink: 0 }}
                    >
                        <Box component="img" src={cfpLogo} alt="Contract Finder Pro" sx={{ height: isCompact ? 54 : 65, width: 'auto' }} />
                    </Box>

                    {/* Referral partner logo */}
                    {refPartner && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 0, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                {refPartner.label && (
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                        In partnership with
                                    </Typography>
                                )}
                                <Box sx={{ backgroundColor: 'white', borderRadius: 1, px: 1, py: 1, display: 'flex', alignItems: 'center' }}>
                                    <Box component="img" src={refPartner.src} alt="Partner" sx={{ height: 30, width: 'auto', maxWidth: 110 }} />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Desktop Nav */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flex: 1, justifyContent: 'flex-end' }}>
                            <Button
                                onMouseEnter={openProducts}
                                onMouseLeave={() => closeProducts(150)}
                                onClick={e => productsAnchor ? setProductsAnchor(null) : openProducts(e)}
                                //onMouseLeave={() => { if (Date.now() - productsOpenedAt.current > 200) closeProducts(150) }}
                                endIcon={
                                    <KeyboardArrowDownIcon sx={{
                                        fontSize: '18px !important',
                                        transition: 'transform 0.2s',
                                        transform: productsAnchor ? 'rotate(180deg)' : 'none',
                                    }} />
                                }
                                sx={{
                                    px: menuitemspx,
                                    color: isProductsActive ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                    fontWeight: isProductsActive ? 600 : 400,
                                    '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.05)' },
                                }}
                            >
                                Products
                            </Button>
                            {!isCompact && (
                                <>
                                    <Button
                                        onMouseEnter={openSolutions}
                                        onMouseLeave={() => { if (Date.now() - solutionsOpenedAt.current > 200) closeSolutions(150) }}
                                        onClick={e => solutionsAnchor ? setSolutionsAnchor(null) : openSolutions(e)}
                                        endIcon={
                                            <KeyboardArrowDownIcon sx={{
                                                fontSize: '18px !important',
                                                transition: 'transform 0.2s',
                                                transform: solutionsAnchor ? 'rotate(180deg)' : 'none',
                                            }} />
                                        }
                                        sx={{
                                            px: menuitemspx,
                                            color: isSolutionsActive ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                            fontWeight: isSolutionsActive ? 600 : 400,
                                            '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.05)' },
                                        }}
                                    >
                                        Solutions
                                    </Button>
                                    <Button
                                        onMouseEnter={openSuccessNetwork}
                                        onMouseLeave={() => { if (Date.now() - successNetworkOpenedAt.current > 200) closeSuccessNetwork(150) }}
                                        onClick={e => successNetworkAnchor ? setSuccessNetworkAnchor(null) : openSuccessNetwork(e)}
                                        endIcon={
                                            <KeyboardArrowDownIcon sx={{
                                                fontSize: '18px !important',
                                                transition: 'transform 0.2s',

                                                transform: successNetworkAnchor ? 'rotate(180deg)' : 'none',
                                            }} />
                                        }
                                        sx={{
                                            px: menuitemspx,
                                            color: isNetworkActive ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                            fontWeight: isNetworkActive ? 600 : 400,
                                            '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.05)' },
                                        }}
                                    >
                                        Network
                                    </Button>
                                </>
                            )}
                            {isCompact && (
                                <Button
                                    onMouseEnter={openCombined}
                                    onMouseLeave={() => { if (Date.now() - combinedOpenedAt.current > 200) closeCombined(150) }}
                                    onClick={e => combinedAnchor ? setCombinedAnchor(null) : openCombined(e)}
                                    endIcon={
                                        <KeyboardArrowDownIcon sx={{
                                            fontSize: '18px !important',
                                            transition: 'transform 0.2s',
                                            transform: combinedAnchor ? 'rotate(180deg)' : 'none',
                                        }} />
                                    }
                                    sx={{
                                        px: menuitemspx,
                                        color: (isSolutionsActive || isNetworkActive) ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                        fontWeight: (isSolutionsActive || isNetworkActive) ? 600 : 400,
                                        '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.05)' },
                                    }}
                                >
                                    Solutions
                                </Button>
                            )}
                            {navLinks.map(link => (
                                <Button
                                    key={link.path}
                                    component={Link}
                                    to={link.path}
                                    sx={{
                                        px: menuitemspx,
                                        color: isActive(link.path) ? 'primary.main' : 'rgba(255,255,255,0.8)',
                                        fontWeight: isActive(link.path) ? 600 : 400,
                                        '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,255,255,0.05)' },
                                        gap: 1,
                                    }}
                                >
                                    {link.live && (
                                        <Box sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                                            <Box sx={{
                                                position: 'absolute', inset: 0, borderRadius: '50%',
                                                backgroundColor: '#22c55e',
                                                '@keyframes livePulse': {
                                                    '0%': { transform: 'scale(1)', opacity: 0.8 },
                                                    '80%': { transform: 'scale(2.2)', opacity: 0 },
                                                    '100%': { transform: 'scale(2.2)', opacity: 0 },
                                                },
                                                animation: 'livePulse 3.8s ease-out infinite',
                                            }} />
                                            <Box sx={{
                                                position: 'absolute', inset: 0, borderRadius: '50%',
                                                backgroundColor: '#22c55e',
                                            }} />
                                        </Box>
                                    )}
                                    {link.label}
                                </Button>
                            ))}
                        </Box>
                    )}

                    {isMobile && <Box sx={{ flex: 1 }} />}

                    {/* Free Trial (primary CTA) + Login (secondary) */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: buttongap, ml: 0 }}>
                            <FancyGoldButton to="/register" sx={{
                                px: regiserpx,
                                py: regiserpy,
                            }}>Free&nbsp;Trial</FancyGoldButton>
                            <FancyLightButton href={loginHref} sx={{
                                px: loginpx,
                                py: loginpy,
                            }}>Login</FancyLightButton>
                        </Box>
                    )}

                    {/* Mobile hamburger */}
                    {isMobile && (
                        <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'white' }}>
                            <MenuIcon />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            {/* Products dropdown */}
            <Menu
                anchorEl={productsAnchor}
                open={Boolean(productsAnchor)}
                onClose={() => setProductsAnchor(null)}
                disableAutoFocusItem
                style={{ pointerEvents: 'none' }}
                PaperProps={{
                    elevation: 4,
                    onMouseEnter: () => { if (productsTimer.current) clearTimeout(productsTimer.current) },
                    onMouseLeave: () => closeProducts(0),
                    style: { pointerEvents: 'auto' },
                    sx: {
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.07)',
                        left: '-250px',
                        px: 1,
                        py: 0,
                        maxWidth: 500,
                        // minWidth: 340,

                    }
                }}
                transformOrigin={{ horizontal: 100, vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {products.map(product => (
                    <MenuItem
                        key={product.path}
                        component={Link}
                        to={product.path}
                        onClick={() => setProductsAnchor(null)}
                        sx={{
                            borderRadius: 1,
                            py: 1.5,
                            px: 1.5,
                            '&:hover': { backgroundColor: `${product.color}30` }
                        }}
                    >
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 1.5,
                            backgroundColor: `${product.color}30`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mr: 2, flexShrink: 0
                        }}>
                            <product.Icon sx={{ color: product.color, fontSize: 20 }} />
                        </Box>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column'
                        }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    {product.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: product.color, fontWeight: 600 }}>
                                    {product.tagline}
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{
                                display: 'flex',
                                // border: BORDER_BLACK,
                                whiteSpace: 'normal'
                            }}>
                                {product.description}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Menu >

            {/* Solutions dropdown */}
            < Menu
                anchorEl={solutionsAnchor}
                open={Boolean(solutionsAnchor)}
                onClose={() => setSolutionsAnchor(null)
                }
                disableAutoFocusItem
                style={{ pointerEvents: 'none' }}
                PaperProps={{
                    elevation: 4,
                    onMouseEnter: () => { if (solutionsTimer.current) clearTimeout(solutionsTimer.current) },
                    onMouseLeave: () => closeSolutions(0),
                    style: { pointerEvents: 'auto' },
                    sx: {
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.07)',
                        px: 1,
                        py: 0,
                        maxWidth: 500,
                        // minWidth: 340,

                    }
                }}
                transformOrigin={{ horizontal: 100, vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {
                    solutionGroups.map((group, gi) => (
                        <Box key={group.group}>
                            {gi > 0 && <Divider sx={{ my: 0.5 }} />}
                            <Typography
                                variant="caption"
                                sx={{ px: 1.5, py: 0.75, display: 'block', color: group.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}
                            >
                                {group.group}
                            </Typography>
                            {group.items.map(item => (
                                <MenuItem
                                    key={item.title}
                                    component={Link}
                                    to={item.path}
                                    onClick={() => setSolutionsAnchor(null)}
                                    sx={{
                                        borderRadius: 1, py: 1,
                                        px: 1.5, '&:hover': { backgroundColor: `${item.color}30` }
                                    }}
                                >
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: 1.5,
                                        backgroundColor: `${item.color}18`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mr: 1.5, flexShrink: 0,
                                    }}>
                                        <item.Icon sx={{ color: item.color, fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} color="text.primary">{item.title}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, t: 0.25, display: 'flex', flexDirection: 'column', whiteSpace: 'normal' }}>{item.description}
                                        </Typography>

                                    </Box>
                                </MenuItem>
                            ))}
                        </Box>
                    ))
                }
            </Menu >

            {/* Success Network dropdown */}
            <Menu
                anchorEl={successNetworkAnchor}
                open={Boolean(successNetworkAnchor)}
                onClose={() => setSuccessNetworkAnchor(null)}
                disableAutoFocusItem
                style={{ pointerEvents: 'none' }}
                PaperProps={{
                    elevation: 4,
                    onMouseEnter: () => { if (successNetworkTimer.current) clearTimeout(successNetworkTimer.current) },
                    onMouseLeave: () => closeSuccessNetwork(0),
                    style: { pointerEvents: 'auto' },
                    sx: { borderRadius: 2, border: '1px solid rgba(0,0,0,0.07)', px: 1, py: 0, maxWidth: 500 },
                }}
                transformOrigin={{ horizontal: 100, vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {successNetwork.map(item => (
                    <MenuItem
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={() => setSuccessNetworkAnchor(null)}
                        sx={{ borderRadius: 1, py: 1.5, px: 1.5, '&:hover': { backgroundColor: `${item.color}30` } }}
                    >
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 1.5,
                            backgroundColor: `${item.color}30`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mr: 2, flexShrink: 0,
                        }}>
                            <item.Icon sx={{ color: item.color, fontSize: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" fontWeight={600} color="text.primary">{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>{item.description}</Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Menu>

            {/* Combined "Solutions & Network" dropdown — only used in compact mode (640–767px) */}
            <Menu
                anchorEl={combinedAnchor}
                open={Boolean(combinedAnchor)}
                onClose={() => setCombinedAnchor(null)}
                disableAutoFocusItem
                style={{ pointerEvents: 'none' }}
                PaperProps={{
                    elevation: 4,
                    onMouseEnter: () => { if (combinedTimer.current) clearTimeout(combinedTimer.current) },
                    onMouseLeave: () => closeCombined(0),
                    style: { pointerEvents: 'auto' },
                    sx: { borderRadius: 2, border: '1px solid rgba(0,0,0,0.07)', px: 1, py: 0, maxWidth: 500 },
                }}
                transformOrigin={{ horizontal: 100, vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            >
                {solutionGroups.map((group, gi) => (
                    <Box key={group.group}>
                        {gi > 0 && <Divider sx={{ my: 0.5 }} />}
                        <Typography
                            variant="caption"
                            sx={{ px: 1.5, py: 0.75, display: 'block', color: group.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}
                        >
                            {group.group}
                        </Typography>
                        {group.items.map(item => (
                            <MenuItem
                                key={item.title}
                                component={Link}
                                to={item.path}
                                onClick={() => setCombinedAnchor(null)}
                                sx={{ borderRadius: 1, py: 1, px: 1.5, '&:hover': { backgroundColor: `${item.color}30` } }}
                            >
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: 1.5,
                                    backgroundColor: `${item.color}18`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    mr: 1.5, flexShrink: 0,
                                }}>
                                    <item.Icon sx={{ color: item.color, fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography variant="body2" fontWeight={600} color="text.primary">{item.title}</Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Box>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Typography
                    variant="caption"
                    sx={{ px: 1.5, py: 0.75, display: 'block', color: COLOR_PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}
                >
                    Success Network
                </Typography>
                {successNetwork.map(item => (
                    <MenuItem
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={() => setCombinedAnchor(null)}
                        sx={{ borderRadius: 1, py: 1, px: 1.5, '&:hover': { backgroundColor: `${item.color}30` } }}
                    >
                        <Box sx={{
                            width: 36, height: 36, borderRadius: 1.5,
                            backgroundColor: `${item.color}18`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            mr: 1.5, flexShrink: 0,
                        }}>
                            <item.Icon sx={{ color: item.color, fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" fontWeight={600} color="text.primary">{item.name}</Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Menu>

            {/* Mobile Drawer */}
            < Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: 290 } }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box component="img" src={cfpLogo} alt="Contract Finder Pro" sx={{ height: 28, width: 'auto' }} />
                            {refPartner && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pl: 1.5, borderLeft: '1px solid rgba(0,0,0,0.12)' }}>
                                    {refPartner.label && (
                                        <Typography sx={{ fontSize: '8px', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                            In partnership with
                                        </Typography>
                                    )}
                                    <Box component="img" src={refPartner.src} alt="Partner" sx={{ height: 18, width: 'auto', maxWidth: 80 }} />
                                </Box>
                            )}
                        </Box>
                        <IconButton onClick={() => setDrawerOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 1 }} />
                    <List dense>
                        <ListItemButton onClick={() => setMobileProductsOpen(!mobileProductsOpen)}>
                            <ListItemText primary="Products" primaryTypographyProps={{ fontWeight: 600 }} />
                            {mobileProductsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </ListItemButton>
                        <Collapse in={mobileProductsOpen}>
                            <List disablePadding>
                                {products.map(product => (
                                    <ListItemButton
                                        key={product.path}
                                        component={Link}
                                        to={product.path}
                                        onClick={() => setDrawerOpen(false)}
                                        sx={{ pl: 4 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <product.Icon sx={{ color: product.color, fontSize: 18 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={product.name}
                                            secondary={product.tagline}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Collapse>
                        <ListItemButton onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}>
                            <ListItemText primary="Solutions" primaryTypographyProps={{ fontWeight: 600 }} />
                            {mobileSolutionsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </ListItemButton>
                        <Collapse in={mobileSolutionsOpen}>
                            <List disablePadding>
                                {solutionGroups.map(group => (
                                    <Box key={group.group}>
                                        <Typography variant="caption" sx={{ pl: 4, pt: 1, pb: 0.25, display: 'block', color: group.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                            {group.group}
                                        </Typography>
                                        {group.items.map(item => (
                                            <ListItemButton
                                                key={item.title}
                                                component={Link}
                                                to={item.path}
                                                onClick={() => setDrawerOpen(false)}
                                                sx={{ pl: 3 }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <item.Icon sx={{ color: item.color, fontSize: 18 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.title}
                                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                />
                                            </ListItemButton>
                                        ))}
                                    </Box>
                                ))}
                            </List>
                        </Collapse>
                        <ListItemButton onClick={() => setMobileSuccessNetworkOpen(!mobileSuccessNetworkOpen)}>
                            <ListItemText primary="Success Network" primaryTypographyProps={{ fontWeight: 600 }} />
                            {mobileSuccessNetworkOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </ListItemButton>
                        <Collapse in={mobileSuccessNetworkOpen}>
                            <List disablePadding>
                                {successNetwork.map(item => (
                                    <ListItemButton
                                        key={item.path}
                                        component={Link}
                                        to={item.path}
                                        onClick={() => setDrawerOpen(false)}
                                        sx={{ pl: 4 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <item.Icon sx={{ color: item.color, fontSize: 18 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.name}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Collapse>
                        {navLinks.map(link => (
                            <ListItemButton
                                key={link.path}
                                component={Link}
                                to={link.path}
                                onClick={() => setDrawerOpen(false)}
                                selected={isActive(link.path)}
                            >
                                {link.live && (
                                    <ListItemIcon sx={{ minWidth: 24 }}>
                                        <Box sx={{ position: 'relative', width: 8, height: 8 }}>
                                            <Box sx={{
                                                position: 'absolute', inset: 0, borderRadius: '50%',
                                                backgroundColor: '#22c55e',
                                                '@keyframes livePulse': {
                                                    '0%': { transform: 'scale(1)', opacity: 0.8 },
                                                    '70%': { transform: 'scale(2.2)', opacity: 0 },
                                                    '100%': { transform: 'scale(2.2)', opacity: 0 },
                                                },
                                                animation: 'livePulse 1.8s ease-out infinite',
                                            }} />
                                            <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                                        </Box>
                                    </ListItemIcon>
                                )}
                                <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: 500 }} />
                            </ListItemButton>
                        ))}
                    </List>
                    <Box sx={{ px: 1, pt: 2 }}>
                        <FancyGoldButton to="/register" fullWidth onClick={() => setDrawerOpen(false)}>
                            Free Trial
                        </FancyGoldButton>
                    </Box>
                    <Box sx={{ px: 1, pt: 1 }}>
                        <FancyLightButton href={loginHref} fullWidth onClick={() => setDrawerOpen(false)}>
                            Login
                        </FancyLightButton>
                    </Box>
                </Box>
            </Drawer >
        </>
    )
}


// const solutionGroups = [
//     {
//         group: 'New to Public Sector',
//         color: COLOR_PRIMARY,
//         items: [
//             { title: 'New to Government Contracts', description: 'Start-up or first-time bidder? Find your first opportunities and build a winning track record from day one', path: '/solutions', Icon: RocketLaunchIcon, color: COLOR_PRIMARY },
//             { title: 'Entering UK Public Sector', description: 'Established business breaking into government? Identify the right opportunities and understand the market fast', path: '/solutions', Icon: DomainIcon, color: COLOR_PRIMARY },
//             { title: 'Exploring the Opportunity', description: 'Assessing whether UK public sector is right for your business? Get the data to make an informed strategic decision', path: '/solutions', Icon: AssessmentIcon, color: COLOR_PRIMARY },
//         ],
//     },
//     {
//         group: 'Scale & Win More',
//         color: COLOR_TERTIARY,
//         items: [
//             { title: 'Improve Efficiency & Win Rates', description: 'Already bidding but want better results with less effort? Sharpen your targeting and improve every submission', path: '/solutions', Icon: TrendingUpIcon, color: COLOR_TERTIARY },
//             { title: 'Expand into New Sectors', description: 'Fast-growing and moving into new markets? Spot opportunities across sectors and manage your expanding pipeline', path: '/solutions', Icon: AccountTreeIcon, color: COLOR_TERTIARY },
//             { title: 'Grow into Europe & Overseas', description: 'Scaling beyond the UK? Monitor international procurement markets and plan your cross-border BD effort', path: '/solutions', Icon: LanguageIcon, color: COLOR_TERTIARY },
//         ],
//     },
//     {
//         group: 'Boost Bid Capability',
//         color: COLOR_SECONDARY,
//         items: [
//             { title: 'Find a Dedicated Bid Partner', description: 'Need expert resource to run your bids? Get matched with a specialist BD partner who knows your sector', path: '/solutions', Icon: HandshakeIcon, color: COLOR_SECONDARY },
//             { title: 'Grow Your Bid Writing Practice', description: 'Bid writer or consultant? Join CFP\'s partner network and receive referrals from clients who need your services', path: '/solutions', Icon: BusinessCenterIcon, color: COLOR_SECONDARY },
//         ],
//     },
//     {
//         group: 'Sell to the Supply Chain',
//         color: COLOR_QUATERNARY,
//         items: [
//             { title: 'Target Public Sector Contractors', description: 'Selling to businesses that supply the public sector? Track contract awards to approach winners at the right moment', path: '/solutions', Icon: StorefrontIcon, color: COLOR_QUATERNARY },
//             { title: 'Vietnamese Business Partnerships', description: 'Looking to invest, sell to, buy from, or partner with Vietnamese businesses? Discover the right connections', path: '/solutions', Icon: PublicIcon, color: COLOR_QUATERNARY },
//         ],
//     },
// ]
